import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { articlesAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import ConfirmModal from '../components/ConfirmModal';
import { FileText, Plus, Edit, Trash2, Eye, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const AdminArticles: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0 });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {}
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const res = await articlesAPI.getAll({ status: '', limit: 1000 });
      const data = res.data.data;
      setArticles(data);
      setStats({
        total: data.length,
        published: data.filter((a: any) => a.status === 'published').length,
        draft: data.filter((a: any) => a.status === 'draft').length,
      });
    } catch (error) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Article',
      message: 'Are you sure you want to delete this article? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await articlesAPI.delete(id);
          toast.success('Article deleted');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchArticles();
        } catch (error) {
          toast.error('Failed to delete');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const handlePublish = async (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Publish Article',
      message: 'Are you sure you want to publish this article? It will be visible to all users.',
      type: 'success',
      confirmText: 'Publish',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await articlesAPI.publish(id);
          toast.success('Article published');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchArticles();
        } catch (error) {
          toast.error('Failed to publish');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const filtered = articles.filter(a =>
    a.title.toLowerCase().includes(filter.toLowerCase()) ||
    a.category?.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) return <AdminLayout><div className="p-8">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Articles</h1>
          <button
            onClick={() => navigate('/admin/articles/create')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus size={20} /> Add Article
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <FileText className="text-blue-600" size={24} />
              <div>
                <p className="text-gray-600 text-sm">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-600" size={24} />
              <div>
                <p className="text-gray-600 text-sm">Published</p>
                <p className="text-2xl font-bold">{stats.published}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <Edit className="text-orange-600" size={24} />
              <div>
                <p className="text-gray-600 text-sm">Draft</p>
                <p className="text-2xl font-bold">{stats.draft}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <input
              type="text"
              placeholder="Search articles..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Published</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((article) => (
                  <tr key={article._id}>
                    <td className="px-6 py-4 font-medium">{article.title}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {article.category || 'other'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        article.status === 'published' ? 'bg-green-100 text-green-800' :
                        article.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {article.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">{article.views}</td>
                    <td className="px-6 py-4 text-sm">
                      {article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {article.status === 'draft' && (
                          <button
                            onClick={() => handlePublish(article._id)}
                            className="text-green-600 hover:text-green-800"
                            title="Publish"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/admin/articles/edit/${article._id}`)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(article._id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          confirmText={confirmModal.confirmText}
          loading={processing}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminArticles;
